import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiErrorHandler } from 'src/hooks/useApiErrorHandler';
import {
  ImageProcessingBatch,
  isOpenBatchStatus,
  useCancelImageProcessingBatchMutation,
  useCreateImageProcessingBatchMutation,
  useLazyGetOpenImageProcessingBatchesQuery,
} from 'src/redux/api/inventory/imageProcessingBatches';
import { getAuth } from 'src/redux/slices/auth';
import { notifyError, notifyWarning } from 'src/utils/notificationHelpers';
import { isVehicleAiImageProcessingEnabled } from 'src/utils/permissionHelpers';
import { v4 as uuidv4 } from 'uuid';

import { getValidationErrorMessage, withPreservedVehicle } from './helpers';

const POLL_INTERVAL_MS = 5000;

type PendingBatchRequest = {
  vehicleId: number;
  mediaIds: number[];
  /** Stable across retries so a timed out request can't end up creating a duplicate batch. */
  idempotencyKey: string;
  /** Selected photos left out of `mediaIds` because they were already enhanced. */
  alreadyEnhancedCount: number;
};

type BulkEnhancementContextValue = {
  openBatches: ImageProcessingBatch[];
  isCreatingBatch: boolean;
  reviewBatch: ImageProcessingBatch | null;
  confirmBatch: PendingBatchRequest | null;
  requestBatchEnhancement: (params: {
    vehicleId: number;
    mediaIds: number[];
    alreadyEnhancedCount?: number;
  }) => void;
  dismissConfirmBatch: () => void;
  confirmBatchEnhancement: () => Promise<void>;
  cancelBatch: (batch: ImageProcessingBatch) => Promise<void>;
  openReviewBatch: (batch: ImageProcessingBatch) => void;
  closeReviewBatch: () => void;
  mergeBatch: (batch: ImageProcessingBatch) => void;
  reportBatchError: (error: unknown) => void;
};

const BulkEnhancementContext = createContext<BulkEnhancementContextValue | null>(null);

const areBatchesEqual = (left: ImageProcessingBatch[], right: ImageProcessingBatch[]) =>
  JSON.stringify(left) === JSON.stringify(right);

export const BulkEnhancementProvider = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const handleError = useApiErrorHandler();
  const handleErrorRef = useRef(handleError);
  const { currentAccount } = useSelector(getAuth);
  const isImageProcessingEnabled = isVehicleAiImageProcessingEnabled(currentAccount);
  const [openBatches, setOpenBatches] = useState<ImageProcessingBatch[]>([]);
  const [reviewBatch, setReviewBatch] = useState<ImageProcessingBatch | null>(null);
  const [confirmBatch, setConfirmBatch] = useState<PendingBatchRequest | null>(null);
  const [fetchOpenBatches] = useLazyGetOpenImageProcessingBatchesQuery();
  const [createBatch, { isLoading: isCreatingBatch }] = useCreateImageProcessingBatchMutation();
  const [cancelBatchRequest] = useCancelImageProcessingBatchMutation();

  handleErrorRef.current = handleError;

  const reportBatchError = useCallback((error: unknown) => {
    const validationMessage = getValidationErrorMessage(error);

    if (validationMessage) {
      notifyError(validationMessage);
      return;
    }

    handleErrorRef.current(error);
  }, []);

  const mergeBatch = useCallback((incoming: ImageProcessingBatch) => {
    setOpenBatches((current) => {
      const existing = current.find((item) => item.id === incoming.id);
      const batch = withPreservedVehicle(incoming, existing);

      if (!isOpenBatchStatus(batch.status)) {
        return existing ? current.filter((item) => item.id !== batch.id) : current;
      }

      if (!existing) {
        return [...current, batch];
      }

      return current.map((item) => (item.id === batch.id ? batch : item));
    });

    setReviewBatch((current) =>
      current?.id === incoming.id ? withPreservedVehicle(incoming, current) : current
    );
  }, []);

  const refreshOpenBatches = useCallback(async () => {
    try {
      const batches = await fetchOpenBatches().unwrap();

      setOpenBatches((current) => (areBatchesEqual(current, batches) ? current : batches));
    } catch (error) {
      handleErrorRef.current(error);
    }
  }, [fetchOpenBatches]);

  // The provider is mounted app-wide, so accounts without the feature never hit the endpoint.
  // Batches are account scoped, hence the reload whenever the active account changes.
  useEffect(() => {
    if (!isImageProcessingEnabled) {
      setOpenBatches([]);
      return;
    }

    void refreshOpenBatches();
  }, [currentAccount?.id, isImageProcessingEnabled, refreshOpenBatches]);

  const hasProcessingBatches = openBatches.some((batch) => batch.status === 'processing');

  // Batches advance on the server's own queue, so the client just re-reads the account endpoint,
  // which already returns a refreshed status plus the vehicle summary for every open batch.
  useEffect(() => {
    if (!hasProcessingBatches) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void refreshOpenBatches();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [hasProcessingBatches, refreshOpenBatches]);

  const requestBatchEnhancement = useCallback(
    ({
      vehicleId,
      mediaIds,
      alreadyEnhancedCount = 0,
    }: {
      vehicleId: number;
      mediaIds: number[];
      alreadyEnhancedCount?: number;
    }) => {
      setConfirmBatch({ vehicleId, mediaIds, idempotencyKey: uuidv4(), alreadyEnhancedCount });
    },
    []
  );

  const dismissConfirmBatch = useCallback(() => {
    setConfirmBatch(null);
  }, []);

  const confirmBatchEnhancement = useCallback(async () => {
    if (!confirmBatch?.mediaIds.length) {
      return;
    }

    try {
      const batch = await createBatch({
        vehicleId: confirmBatch.vehicleId,
        mediaIds: confirmBatch.mediaIds,
        idempotencyKey: confirmBatch.idempotencyKey,
      }).unwrap();

      mergeBatch(batch);
      setConfirmBatch(null);

      if (batch.skipped.length) {
        notifyWarning(
          t('vehicleScreen:bulk_enhance_skipped', { count: batch.skipped.length })
        );
      }

      // The create response has no vehicle summary, so pull it in before the first toast renders.
      await refreshOpenBatches();
    } catch (error) {
      reportBatchError(error);
    }
  }, [confirmBatch, createBatch, mergeBatch, refreshOpenBatches, reportBatchError, t]);

  const cancelBatch = useCallback(
    async (batch: ImageProcessingBatch) => {
      try {
        await cancelBatchRequest({ vehicleId: batch.vehicle_id, batchId: batch.id }).unwrap();
        // A canceled batch stays open while it still holds reviewable results, so let the
        // server decide whether it should keep showing up.
        await refreshOpenBatches();
      } catch (error) {
        reportBatchError(error);
      }
    },
    [cancelBatchRequest, refreshOpenBatches, reportBatchError]
  );

  const openReviewBatch = useCallback((batch: ImageProcessingBatch) => {
    setReviewBatch(batch);
  }, []);

  const closeReviewBatch = useCallback(() => {
    setReviewBatch(null);
    void refreshOpenBatches();
  }, [refreshOpenBatches]);

  const value = useMemo(
    () => ({
      openBatches,
      isCreatingBatch,
      reviewBatch,
      confirmBatch,
      requestBatchEnhancement,
      dismissConfirmBatch,
      confirmBatchEnhancement,
      cancelBatch,
      openReviewBatch,
      closeReviewBatch,
      mergeBatch,
      reportBatchError,
    }),
    [
      openBatches,
      isCreatingBatch,
      reviewBatch,
      confirmBatch,
      requestBatchEnhancement,
      dismissConfirmBatch,
      confirmBatchEnhancement,
      cancelBatch,
      openReviewBatch,
      closeReviewBatch,
      mergeBatch,
      reportBatchError,
    ]
  );

  return (
    <BulkEnhancementContext.Provider value={value}>{children}</BulkEnhancementContext.Provider>
  );
};

export const useBulkEnhancement = () => {
  const context = useContext(BulkEnhancementContext);

  if (!context) {
    throw new Error('useBulkEnhancement must be used within BulkEnhancementProvider');
  }

  return context;
};

export default BulkEnhancementContext;
